import React, { Dispatch } from "react";
import { Icon } from "@cadmakers-ui/icons";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { Button, Form } from "reactstrap";
import { Comment as UIComment } from "semantic-ui-react";
import { endpoints } from "../../../Constants/endpoints";
import "../../../Styles/Comment.scss";
import { Image } from "../../../Models/Image";
import "../../../Styles/CommentBox.scss";
import { ToggleImagesButton } from "./ToggleImagesButton";
import { fileService, imageUploadService } from "../../../Services";
import FileUploadButton from "../../../../Common/Components/FileUploadButton/FileUploadButton";
import ElementSpacer from "../../../../Layout/Components/ElementSpacer";
import { Comment, CreateComment, EditComment } from "../../../Models/Comment";
import { ImageGallery } from "../ImageGallery";
import { DateDisplay, TextDisplay } from "../../../../Common";
import { DeleteImage } from "../../../Models/DeleteImage";
import CommentTextWithPings from "./CommentTextWithPings";
import { commentRestrictions } from "../../../Constants/commentRestrictions";
import { CommentLikeButton } from "./CommentLikeButton";
import { CommentInput } from "./CommentInput";
import iconStyles from "../../../../Common/Styles/Icons.module.scss";

const uuid = require("uuid/v1");

type Props = {
  depth: number;
  comment: Comment;
  code: string;
  activeReplyId: number | null;
  activeEditId: number | null;
  childComments: Comment[];
  shouldAutocompleteForOtherCompanyUsers: boolean;
  setActiveReplyId: (id: number | null) => void;
  setActiveEditId: (id: number | null) => void;
  createReplyHander: (comment: CreateComment, success: () => void) => void;
  editCommentHandler: (
    commentId: number,
    comment: EditComment,
    newComments: Image[],
    isDeleted: boolean,
    action: () => void
  ) => void;
  deleteCommentHandler: (id: number) => void;
  onLike: (commentId: number) => void;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  commentReply: string;
  commentEdit: string;
  depth: number;
  showImages: boolean;
  replyUuid: string;
  replyImages: Image[];
  editImages: Image[];
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class CommentItem extends React.Component<Props & DispatchProps, State> {
  private replyUploadPendingCount: number;

  private editUploadPendingCount: number;

  constructor(props: Props & DispatchProps) {
    super(props);

    this.state = {
      commentReply: "",
      commentEdit: "",
      depth: this.props.depth,
      showImages: this.props.comment.images.length > 0,
      replyUuid: "",
      replyImages: [],
      editImages: []
    };

    this.replyUploadPendingCount = 0;
    this.editUploadPendingCount = 0;
  }

  onReplyClick = () => {
    if (this.props.activeReplyId !== this.props.comment.id) {
      this.props.setActiveReplyId(this.props.comment.id);

      this.setState({
        replyUuid: uuid(),
        commentReply: "",
        replyImages: []
      });
    } else {
      this.closeReply();
    }
  };

  onEditClick = () => {
    if (this.props.activeEditId !== this.props.comment.id) {
      this.props.setActiveEditId(this.props.comment.id);
      this.setState({
        commentEdit: this.props.comment.originalCommentText,
        editImages: this.props.comment.images,
        showImages: false
      });
    } else {
      this.closeReply();
    }
  };

  onDeleteClick = () => {
    this.props.deleteCommentHandler(this.props.comment.id);
  };

  handleReplyChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      commentReply: event.target.value
    });
  };

  handleEditChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      commentEdit: event.target.value
    });
  };

  resetFields = () => {
    this.setState({
      commentReply: "",
      commentEdit: ""
    });
  };

  closeReply = () => {
    this.resetFields();
    this.props.setActiveReplyId(null);
  };

  clearReply = () => {
    this.setState({
      commentReply: ""
    });
  };

  closeEdit = () => {
    this.resetFields();
    this.props.setActiveEditId(null);
  };

  clearEdit = () => {
    this.setState({
      commentEdit: ""
    });
  };

  onToggleImagesClick = () => {
    this.setState(
      prevState => ({
        showImages: !prevState.showImages
      }),
      () => this.props.setActiveEditId(null)
    );
  };

  createReply = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const reply: CreateComment = {
      code: this.props.code,
      comment: this.state.commentReply,
      parentCommentId: this.props.comment.id,
      imageRefs: this.state.replyImages
    };

    this.props.createReplyHander(reply, () => {
      this.setState({
        replyImages: []
      });
    });
  };

  editComment = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const comment: EditComment = {
      comment: this.state.commentEdit,
      imageRefs: this.state.editImages
    };

    this.props.editCommentHandler(
      this.props.comment.id,
      comment,
      this.state.editImages,
      this.props.comment.isDeleted,
      () => {
        this.setState({
          editImages: []
        });
      }
    );
  };

  onReplyImagesAdded = (images: Image[]) => {
    this.replyUploadPendingCount += images.length;

    images.forEach(async image => {
      await imageUploadService.uploadImage(
        endpoints.comments.images.uploadTemp(this.state.replyUuid),
        image,
        newImage => {
          this.setState(prevState => ({
            replyImages: prevState.replyImages.concat(newImage)
          }));
        },
        hash => {
          return this.state.replyImages.find(x => x.fullSizeHash === hash);
        },
        () => {
          this.showErrorSnackbar("Failed to upload image", "This image has already been uploaded");
        },
        (response, result) => {
          this.showErrorSnackbar("Failed to upload image", result.error.message, response.status);
        },
        response => {
          this.showErrorSnackbar(
            "Failed to upload image",
            "An unknown error has occured",
            response.status
          );
        }
      );

      this.replyUploadPendingCount--;
    });
  };

  onReplyImageDelete = (image: Image) => {
    const deleteModel: DeleteImage = {
      fullSizeObjectPath: image.fullSizeObjectPath,
      thumbnailObjectPath: image.thumbnailObjectPath
    };

    imageUploadService.deleteImage(
      endpoints.comments.images.deleteTemp(),
      deleteModel,
      () => {
        this.setState(prevState => ({
          replyImages: prevState.replyImages.filter(x => x.fullSizeHash !== image.fullSizeHash)
        }));
      },
      (response, result) => {
        this.showErrorSnackbar("Failed to remove image", result.error.message, response.status);
      },
      response => {
        this.showErrorSnackbar(
          "Failed to remove image",
          "An unknown error has occured",
          response.status
        );
      }
    );
  };

  onEditImagesAdded = (images: Image[]) => {
    this.editUploadPendingCount += images.length;

    images.forEach(async image => {
      await imageUploadService.uploadImage(
        endpoints.comments.images.uploadTemp(this.props.comment.id),
        image,
        newImage => {
          this.setState(prevState => ({
            editImages: prevState.editImages.concat(newImage)
          }));
        },
        hash => {
          return this.state.editImages.find(x => x.fullSizeHash === hash);
        },
        () => {
          this.showErrorSnackbar("Failed to upload image", "This image has already been uploaded");
        },
        (response, result) => {
          this.showErrorSnackbar("Failed to upload image", result.error.message, response.status);
        },
        response => {
          this.showErrorSnackbar(
            "Failed to upload image",
            "An unknown error has occured",
            response.status
          );
        }
      );

      this.editUploadPendingCount--;
    });
  };

  onEditImageDelete = (image: Image) => {
    if (image.fullSizeObjectPath) {
      this.setState(prevState => ({
        editImages: prevState.editImages.filter(x => x.fullSizeHash !== image.fullSizeHash)
      }));
    }
  };

  onReplyUploadClick = (files: File[]) => {
    this.onReplyImagesAdded(fileService.filestoImages(files));
  };

  onEditUploadClick = (files: File[]) => {
    this.onEditImagesAdded(fileService.filestoImages(files));
  };

  showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    let label = `${reason}: ${errorDescription}`;

    if (errorCode) {
      label += `(Error Code ${errorCode})`;
    }

    this.props.showSnack("COMMENT_ITEM", {
      label,
      timeout: 5000
    });
  };

  render() {
    const commentTextClass = this.props.comment.isDeleted ? "deleted" : "";

    return (
      <UIComment>
        <UIComment.Avatar as="a" src="https://react.semantic-ui.com/images/avatar/small/matt.jpg" />
        <UIComment.Content>
          <UIComment.Author as="a">{this.props.comment.displayName}</UIComment.Author>
          <UIComment.Metadata>
            <DateDisplay date={new Date(Date.parse(this.props.comment.postedAt))} />
          </UIComment.Metadata>
          <UIComment.Text className={commentTextClass}>
            <TextDisplay>
              <CommentTextWithPings comment={this.props.comment} />
            </TextDisplay>
          </UIComment.Text>
          {this.state.showImages ? (
            <>
              <ImageGallery images={this.props.comment.images} />
              <br />
            </>
          ) : (
            ""
          )}
          <UIComment.Actions>
            {this.state.depth < commentRestrictions.maxReplies ? (
              <a onClick={this.onReplyClick} className="disable-double-click-select">
                Reply
              </a>
            ) : (
              ""
            )}
            {this.props.comment.permissions.canEdit && !this.props.comment.isDeleted ? (
              <a onClick={this.onEditClick} className="disable-double-click-select">
                Edit
              </a>
            ) : (
              ""
            )}
            {this.props.comment.permissions.canDelete && !this.props.comment.isDeleted ? (
              <a onClick={this.onDeleteClick} className="disable-double-click-select">
                Delete
              </a>
            ) : (
              ""
            )}

            {!this.props.comment.isDeleted && (
              <CommentLikeButton
                commentId={this.props.comment.id}
                voteCount={this.props.comment.voteCount}
                existingVote={this.props.comment.loggedInUserVote}
                onLike={this.props.onLike}
              />
            )}

            {!this.props.comment.isDeleted && this.props.comment.images.length > 0 ? (
              <ToggleImagesButton
                onToggle={this.onToggleImagesClick}
                imageCount={this.props.comment.images.length}
                toggle={this.state.showImages}
              />
            ) : (
              ""
            )}
          </UIComment.Actions>
        </UIComment.Content>
        {this.props.activeReplyId === this.props.comment.id ? (
          <div>
            <Form className="ui reply form" onSubmit={this.createReply} acceptCharset="UTF-8">
              <CommentInput
                value={this.state.commentReply}
                onValueChange={this.handleReplyChange}
                onValueClear={this.clearReply}
                images={this.state.replyImages}
                onImagesAdded={this.onReplyImagesAdded}
                onImageDelete={this.onReplyImageDelete}
                shouldAutocompleteForOtherCompanyUsers={
                  this.props.shouldAutocompleteForOtherCompanyUsers
                }
              />
              <ElementSpacer>
                <Button color="primary" className="comment-button">
                  <span>
                    <Icon
                      appName="GeneralLayout"
                      assetName="GENERAL_L_CreateEdit"
                      className={iconStyles.smallIcon}
                    />
                  </span>
                  Add Reply
                </Button>
                <FileUploadButton className="comment-button" onClick={this.onReplyUploadClick}>
                  Upload Images
                </FileUploadButton>
              </ElementSpacer>
            </Form>
          </div>
        ) : (
          ""
        )}

        {this.props.activeEditId === this.props.comment.id ? (
          <div>
            <Form className="ui reply form" onSubmit={this.editComment} acceptCharset="UTF-8">
              <CommentInput
                value={this.state.commentEdit}
                onValueChange={this.handleEditChange}
                onValueClear={this.clearEdit}
                images={this.state.editImages}
                onImagesAdded={this.onEditImagesAdded}
                onImageDelete={this.onEditImageDelete}
                shouldAutocompleteForOtherCompanyUsers={
                  this.props.shouldAutocompleteForOtherCompanyUsers
                }
              />
              <ElementSpacer>
                <Button color="primary" className="comment-button">
                  <span>
                    <Icon
                      appName="GeneralLayout"
                      assetName="GENERAL_L_CreateEdit"
                      className={iconStyles.smallIcon}
                    />
                  </span>
                  Save
                </Button>
                <FileUploadButton className="comment-button" onClick={this.onEditUploadClick}>
                  Upload Images
                </FileUploadButton>
              </ElementSpacer>
            </Form>
          </div>
        ) : (
          ""
        )}

        {this.props.childComments.length > 0 ? (
          <UIComment.Group>
            {this.props.childComments.map((comment: Comment) => {
              return (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  code={this.props.code}
                  activeReplyId={this.props.activeReplyId}
                  activeEditId={this.props.activeEditId}
                  setActiveReplyId={this.props.setActiveReplyId}
                  setActiveEditId={this.props.setActiveEditId}
                  createReplyHander={this.props.createReplyHander}
                  editCommentHandler={this.props.editCommentHandler}
                  deleteCommentHandler={this.props.deleteCommentHandler}
                  childComments={comment.childComments}
                  depth={this.state.depth + 1}
                  onLike={this.props.onLike}
                  showSnack={this.props.showSnack}
                  shouldAutocompleteForOtherCompanyUsers={
                    this.props.shouldAutocompleteForOtherCompanyUsers
                  }
                />
              );
            })}
          </UIComment.Group>
        ) : (
          ""
        )}
      </UIComment>
    );
  }
}

export default connect(null, mapDispatchToProps)(CommentItem);
